import { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import { api } from "../api";

// A word this system uses, with what it means attached.
//
// ONE SOURCE. The definitions come from `/glossary`, which serves
// `backend/writing/serving/glossary.json` — the same file `docs/GLOSSARY.md` is generated from.
// Hardcoding the glosses here would make a second set of sentences to keep in agreement, and
// `tests/test_glossary.py` asserts the other direction too: a `<Term name="...">` naming something
// with no entry fails the build rather than rendering a word with no help behind it.
//
// HOVER AND TAP. A `title` attribute would have been free and does nothing on a touch screen, so
// this is a real popover: hover opens it, click or Enter toggles it, Escape and clicking away close
// it. A term nobody can reach on a phone is help for some readers only.
//
// IF THE FETCH FAILS the word renders as plain text with no marking. A dotted underline promising
// an explanation that never arrives is worse than an unmarked word.

interface Entry {
  term: string;
  also?: string[];
  gloss: string;
}

const GlossaryContext = createContext<Map<string, Entry> | null>(null);

/** Fetches the glossary once and provides it. Mounted high; safe to render before it resolves. */
export function GlossaryProvider({ children }: { children: React.ReactNode }) {
  const [terms, setTerms] = useState<Map<string, Entry> | null>(null);

  useEffect(() => {
    let live = true;
    api.get<{ terms: Entry[] }>("/glossary")
      .then((v) => {
        if (!live) return;
        const map = new Map<string, Entry>();
        for (const entry of v.terms ?? []) {
          map.set(entry.term, entry);
          // Aliases resolve to the same entry, so a screen can mark the word it actually shows —
          // "folder of work" rather than "activity".
          for (const alias of entry.also ?? []) map.set(alias, entry);
        }
        setTerms(map);
      })
      // Not an error worth showing. Help is an addition; its absence is not a failure of the page.
      .catch(() => setTerms(null));
    return () => { live = false; };
  }, []);

  return <GlossaryContext.Provider value={terms}>{children}</GlossaryContext.Provider>;
}

export function Term({ name, children }: { name: string; children?: React.ReactNode }) {
  const terms = useContext(GlossaryContext);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const id = useId();
  const entry = terms?.get(name);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const label = children ?? name;
  if (!entry) return <>{label}</>;

  return (
    <span className="gt-wrap" ref={wrap}
          onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span className="gt-term" role="button" tabIndex={0} aria-describedby={open ? id : undefined}
            onClick={() => setOpen((o) => !o)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); }
            }}>
        {label}
      </span>
      {open && (
        <span className="gt-pop" id={id} role="tooltip">
          <b>{entry.term}</b> — {entry.gloss}
        </span>
      )}
    </span>
  );
}
